import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

// @mui
import { useTheme } from '@mui/material/styles';
import { Grid, Container, FormControl, Select, MenuItem, MenuItemCard, CardContent, Typography, Card ,Box } from '@mui/material';
import {
  AppCurrentVisits,
  AppWebsiteVisits,
  AppWidgetSummary,
} from '../sections/@dashboard/app';
import { getInterpreterDashboard, getDashboardTrends, getDashboardGroupChart, getAverageTurnaroundTime, getExpertAnalysisTime } from 'api/dashboard';
import { useSnackbar } from '../components/snackbar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ScienceIcon from '@mui/icons-material/Science';

// ----------------------------------------------------------------------

export default function DashboardSuperUserPage() {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [pieLoading, setPieLoading] = useState(true);
  const [allCount, setAllCount] = useState({ totalReports: 0, todayCompleted: 0, todayRequested: 0, totalCompleted: 0, totalPending: 0 });
  const [trends, setTrends] = useState([]);
  const [groupData, setGroupData] = useState([]);
  const { totalReports, todayCompleted, todayRequested, totalCompleted, totalPending } = allCount;
  const [allExpertsTotal, setAllExpertsTotal] = useState(0);

  const [selectedRange, setSelectedRange] = useState('lastMonth');
  const [tatData, setTatData] = useState(null);
  const [tatLoading, setTatLoading] = useState(true);

  const [selectTATRange, setSelectTATRange] = useState('lastWeek');
  const [expertAnalysisData, setExpertAnalysisData] = useState([]);
  const [expertLoading, setExpertLoading] = useState(true);



  useEffect(() => {
    fetchData();
    fetchTrendsData();
  }, []);

  useEffect(() => {
    fetchPieChartData(selectedRange);
  }, [selectedRange]);

  useEffect(() => {
    fetchTurnaroundData(selectTATRange);
    fetchExpertAnalysisData(selectTATRange);
  }, [selectTATRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getInterpreterDashboard({});
      setLoading(false);
      if (response && response.success) {
        if (response.data) {
          setAllCount(response.data);
        }
      } else {
        if (response && response.message) {
          enqueueSnackbar(response.message, { variant: 'error' })

        }
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const fetchTrendsData = async () => {
    try {
      const response = await getDashboardTrends({});
      setTrendsLoading(false);
      if (response && response.success) {
        setTrends(response?.data || []);
      }
    } catch (error) {
      setTrendsLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const fetchPieChartData = async (range) => {
    try {
      setPieLoading(true);
      const response = await getDashboardGroupChart({ range });
      setPieLoading(false);
      if (response?.success && response?.data) {
        setAllExpertsTotal(response.data.All || 0);
        const mapGroupChartData = Object.keys(response.data)
          .filter((key) => key !== "All") // exclude the "All" key
          .map((key) => ({
            label: key,
            value: response.data[key],
          }));
        setGroupData(mapGroupChartData);
      }
    } catch (error) {
      setPieLoading(false);
      console.error("Error fetching group chart data:", error);
    }
  };
  const fetchTurnaroundData = async (range) => {
    try {
      setTatLoading(true);
      const response = await getAverageTurnaroundTime({ range }); // <-- backend endpoint you created
      setTatLoading(false);
      if (response?.success && response?.data) setTatData(response.data);
    } catch (error) {
      setTatLoading(false);
      console.error("Error fetching turnaround time:", error);
    }
  };
  const fetchExpertAnalysisData = async (range) => {
    try {
      setExpertLoading(true);
      const response = await getExpertAnalysisTime({ range }); // 👈 create this API call in api/dashboard.js
      setExpertLoading(false);

      if (response?.success && response?.data) {
        setExpertAnalysisData(response.data);
      } else {
        setExpertAnalysisData([]);
      }
    } catch (error) {
      setExpertLoading(false);
      console.error("Error fetching expert analysis data:", error);
    }
  };


  return (
    <>
      <Helmet>
        <title>{`Dashboard | ${process.env.REACT_APP_PORTAL_NAME}`}</title>
      </Helmet>

      {/* <Container maxWidth="xl"> */}
      <Container maxWidth="xl" sx={{ pb: 10, overflowY: 'auto' }}>



        <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
          <Grid item xs={12} sm={6} md={2}>
            <AppWidgetSummary title="Today's ECGs" total={totalReports || 0} loading={loading} icon={'solar:documents-bold-duotone'} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <AppWidgetSummary title="Today's review reqests" total={todayRequested || 0} loading={loading} icon={'solar:documents-bold-duotone'} />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <AppWidgetSummary title="Today's reviewed" total={todayCompleted || 0} loading={loading} color="info" icon={'healthicons:i-documents-accepted'} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary title="Completed in last 30 day's" total={totalCompleted || 0} loading={loading} color="warning" icon={'uiw:document'} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary title="Pending in last 30 day's" total={totalPending || 0} loading={loading} color="error" icon={'arcticons:onlyoffice-documents'} />
          </Grid>


          <Grid item xs={12} md={6} lg={8}>
            <AppWebsiteVisits
              title="Monthly trends"
              subheader="current year's"
              loading={trendsLoading}
              chartLabels={trends?.map(item => item?.month)}
              chartData={[
                {
                  name: 'Reports',
                  type: 'area',
                  fill: 'gradient',
                  data: trends?.map(item => item?.requested),
                },
                {
                  name: 'Interpreted',
                  type: 'line',
                  fill: 'solid',
                  data: trends?.map(item => item?.completed),
                },
                {
                  name: 'Pending',
                  type: 'column',
                  fill: 'gradient',
                  data: trends?.map(item => item?.pending),
                },
              ]}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>

            <AppCurrentVisits
              title="Expert-wise Interpreted"
              subheader={
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 120,
                    '& .MuiOutlinedInput-root': { height: 32 },
                    '& .MuiSelect-select': { py: 0.5, fontSize: 13 },
                  }}
                >
                  <Select
                    value={selectedRange}
                    onChange={(e) => setSelectedRange(e.target.value)}
                  >
                    <MenuItem value="lastWeek">Last 7 days</MenuItem>
                    <MenuItem value="lastMonth">Last 30 days</MenuItem>
                  </Select>
                </FormControl>
              }
              loading={pieLoading}
              chartData={groupData}
              chartColors={[
                theme.palette.info.main,
                theme.palette.warning.main,
                theme.palette.error.main,
              ]}
              totalLabel={`Total Reports: ${allExpertsTotal}`}
            />

          </Grid>

          <Grid item xs={12}>
            <Card sx={{ mt: 3, p: 3, borderRadius: 3, boxShadow: 2 }}>
              <CardContent>


                <Grid container justifyContent="space-between" alignItems="center">
                  <Grid item>
                    <Typography variant="h5" gutterBottom>
                      Turnaround Time
                    </Typography>
                  </Grid>

                  <Grid item>
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 120,
                        '& .MuiOutlinedInput-root': { height: 32 },
                        '& .MuiSelect-select': { py: 0.5, fontSize: 13 },
                      }}
                    >
                      <Select
                        value={selectTATRange}
                        onChange={(e) => {
                          setSelectTATRange(e.target.value);
                          fetchTurnaroundData(e.target.value); // Trigger API on change
                        }}
                      >
                        <MenuItem value="lastWeek">This Week</MenuItem>

                        <MenuItem value="lastMonth">This Month</MenuItem>

                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {tatLoading ? (
                  <Typography variant="body2">Loading...</Typography>
                ) : tatData ? (
                  <>
                    <Grid container spacing={3} sx={{ mt: 1, mb: 3 }}>
                      {/* Total Turnaround Time */}
                      <Grid item xs={12} md={4}>
                        <Card
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            height: '100%',
                          }}
                        >
                          <AccessTimeIcon sx={{ color: 'primary.main', fontSize: 36, mr: 2 }} />

                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Total Turnaround Time
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 0.5 }}>
                              {tatData.readable?.averageTurnaround || 'N/A'}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>

                      {/* Queue Time */}
                      <Grid item xs={12} md={4}>
                        <Card
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            height: '100%',
                          }}
                        >
                          <HourglassEmptyIcon sx={{ color: 'primary.main', fontSize: 36, mr: 2 }} />

                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Queue Time
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 0.5 }}>
                              {tatData.readable?.averageQueue || 'N/A'}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>

                      {/* Analysis Time */}
                      <Grid item xs={12} md={4}>
                        <Card
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            height: '100%',
                          }}
                        >
                          <ScienceIcon sx={{ color: 'primary.main', fontSize: 36, mr: 2 }} />

                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Analysis Time
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 0.5 }}>
                              {tatData.readable?.averageAnalysis || 'N/A'}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>
                    </Grid>


                 <Grid container spacing={3} sx={{ mt: 4 }}>

                      <Grid item xs={12} >
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          Analysis Time by Experts
                        </Typography>

                        {expertLoading ? (
                          <Typography variant="body2">Loading...</Typography>
                        ) : expertAnalysisData.length > 0 ? (
                          expertAnalysisData.map((expert, idx) => (
                            <Card
                              key={idx}
                              sx={{
                                mb: 1,
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                bgcolor: 'grey.50',
                                borderRadius: 2,
                              }}
                            >
                              <Typography variant="body2">{expert.name}</Typography>
                              <Typography variant="body2" color="success.main">
                                {expert.readable?.averageAnalysis || '-'}
                              </Typography>
                            </Card>
                          ))
                        ) : (
                          <Typography variant="body2">No data available</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <Typography variant="body2">No data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </Container>
    </>
  );
}
